import { useEffect, useRef, useState } from 'react';
import type {
  CsvProblem,
  CsvRow,
  RowToSkip,
  WhatsappTemplate,
} from '../types/whatsapp';
import SendCheckSummary from './SendCheckSummary';
import SheetPreviewTable from './SheetPreviewTable';
import WhatsappMessagePreview from './WhatsappMessagePreview';
import { useDialogFocus } from '../hooks/useDialogFocus';

function sendButtonLabel(sendCount: number, skipCount: number): string {
  if (skipCount === 0) return `Send to ${sendCount}`;

  return `Skip ${skipCount} and send to ${sendCount}`;
}

interface ConfirmSendModalProps {
  isOpen: boolean;
  isSending: boolean;
  uploadPercent: number;
  campaignName: string;
  sheetProblems: CsvProblem[];
  rowsToSend: CsvRow[];
  rowsToSkip: RowToSkip[];
  variableNumbers: number[];
  template: WhatsappTemplate | null;
  mediaUrl?: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

export default function ConfirmSendModal({
  isOpen,
  isSending,
  uploadPercent,
  campaignName,
  sheetProblems,
  rowsToSend,
  rowsToSkip,
  variableNumbers,
  template,
  mediaUrl,
  onClose,
  onConfirm,
}: ConfirmSendModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [typedName, setTypedName] = useState('');

  const canSend = sheetProblems.length === 0 && rowsToSend.length > 0;
  const skipCount = rowsToSkip.length;
  const firstRow = rowsToSend[0];
  const nameMatches = typedName.trim() === campaignName.trim();

  useEffect(() => {
    if (!isOpen) setTypedName('');
  }, [isOpen]);

  useDialogFocus(panelRef, { isOpen, isBusy: isSending, onClose });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={isSending ? undefined : onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-send-title"
        className="relative flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h3
            id="confirm-send-title"
            className="text-lg font-semibold text-gray-900"
          >
            {campaignName}
          </h3>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
          <SendCheckSummary
            sheetProblems={sheetProblems}
            sendCount={rowsToSend.length}
            rowsToSkip={rowsToSkip}
          />

          {canSend && (
            <SheetPreviewTable
              rows={rowsToSend}
              variableNumbers={variableNumbers}
            />
          )}

          {canSend && firstRow && template && (
            <div>
              <h4 className="mb-2 text-sm font-medium text-gray-700">
                What row {firstRow.lineNumber} will receive
              </h4>
              <WhatsappMessagePreview
                template={template}
                values={firstRow.cells}
                mediaUrl={mediaUrl}
              />
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4">
          {canSend && (
            <div className="mb-4">
              <label
                htmlFor="confirm-campaign-name"
                className="mb-1 block text-sm text-gray-700"
              >
                Type <span className="font-semibold">{campaignName}</span> to
                send
              </label>
              <input
                id="confirm-campaign-name"
                type="text"
                autoComplete="off"
                value={typedName}
                disabled={isSending}
                onChange={(event) => setTypedName(event.target.value)}
                placeholder={campaignName}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSending}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {canSend ? 'Cancel' : 'Close'}
            </button>
            {canSend && (
              <button
                type="button"
                onClick={onConfirm}
                disabled={isSending || !nameMatches}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSending
                  ? `Uploading ${uploadPercent}%`
                  : sendButtonLabel(rowsToSend.length, skipCount)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
